import { ChevronLeft, ChevronRight, Headphones, Pause, Play, Settings2, Square, Volume2, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNarration } from './NarrationProvider';

export function NarrationDock() {
  const { t } = useTranslation();
  const {
    supported,
    tracks,
    status,
    activeTrack,
    activeIndex,
    rate,
    voices,
    selectedVoiceURI,
    playPage,
    pause,
    resume,
    stop,
    previous,
    next,
    setRate,
    setVoice,
    previewVoice,
  } = useNarration();
  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  if (!supported || !tracks.length) return null;
  const isActive = status === 'playing' || status === 'paused';
  const title = activeTrack?.title ?? tracks[0]?.title ?? t('narration.listenPage');

  if (!open && !isActive) {
    return (
      <button type="button" className="narration-fab" onClick={() => setOpen(true)} aria-label={t('narration.openPlayer')}>
        <Headphones size={20} />
        <span>{t('narration.listen')}</span>
      </button>
    );
  }

  return (
    <aside className={`narration-dock ${isActive ? 'is-active' : ''}`} aria-label={t('narration.player')}>
      <div className="narration-dock__top">
        <span className="narration-dock__icon"><Volume2 size={18} /></span>
        <div>
          <small>{status === 'playing' ? t('narration.nowPlaying') : status === 'paused' ? t('narration.paused') : t('narration.ready')}</small>
          <strong>{title}</strong>
        </div>
        <button type="button" onClick={() => { stop(); setOpen(false); setSettingsOpen(false); }} aria-label={t('common.close')}><X size={16} /></button>
      </div>

      <div className="narration-dock__progress" aria-hidden="true">
        {tracks.map((track, index) => <i key={track.id} className={index <= activeIndex ? 'is-filled' : ''} />)}
      </div>

      <div className="narration-dock__controls">
        <button type="button" onClick={previous} disabled={!isActive}><ChevronLeft size={18} /></button>
        <button type="button" className="is-primary" onClick={status === 'playing' ? pause : status === 'paused' ? resume : playPage}>
          {status === 'playing' ? <Pause size={19} /> : <Play size={19} />}
        </button>
        <button type="button" onClick={next} disabled={!isActive}><ChevronRight size={18} /></button>
        <button type="button" onClick={stop} disabled={!isActive}><Square size={15} /></button>
        <button type="button" className={settingsOpen ? 'is-selected' : ''} onClick={() => setSettingsOpen((value) => !value)}><Settings2 size={17} /></button>
      </div>

      {settingsOpen && (
        <div className="narration-settings">
          <label>
            <span>{t('narration.voice')}</span>
            <select value={selectedVoiceURI} onChange={(event) => setVoice(event.target.value)}>
              {voices.map((voice) => <option key={voice.voiceURI} value={voice.voiceURI}>{voice.name} · {voice.lang}</option>)}
            </select>
          </label>
          <div className="narration-settings__rate">
            <span>{t('narration.speed')}</span>
            {[0.85, 0.92, 1, 1.12].map((value) => (
              <button key={value} type="button" className={Math.abs(rate - value) < .01 ? 'is-active' : ''} onClick={() => setRate(value)}>{value}×</button>
            ))}
          </div>
          <button type="button" className="narration-settings__preview" onClick={() => previewVoice(selectedVoiceURI)}><Play size={14} /> {t('narration.preview')}</button>
          {!voices.length && <small>{t('narration.noVoice')}</small>}
        </div>
      )}
    </aside>
  );
}
